/**
 * Posting a listing, step two: the posting itself.
 *
 * This is where Stripe returns to. The reservation is paid for by the time
 * anyone reads this page, so nothing here can lose the payment — the only
 * question left is what the listing says, and submitting that publishes it.
 *
 * Two things are handled carefully. The first is the gap between the card
 * clearing and the webhook landing: the page polls, and after a couple of
 * attempts asks the server to read the session back from Stripe itself, so a
 * slow delivery is a few seconds of "confirming" rather than a dead end.
 *
 * The second is the import. Pasting the posting's own address fills the form
 * in, but a page that refuses automated readers, an allowance that has run
 * out, and a key that was never configured are all ordinary weather. Every one
 * of them lands in the same place: the form, empty, with a line saying what
 * happened and the apply link already filled in. Manual entry is always
 * available, and nothing about the reservation changes either way.
 */

import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useRef, useState, type ReactNode } from 'react';

import {
  LISTING_LIFETIME_DAYS,
  hasListingContent,
  isPaidAwaitingContent,
  type Company,
  type MyListing,
  type ParseJdResponse,
} from '@bidtohire/shared';

import { JobForm } from '../components/JobForm';
import { Money } from '../components/Money';
import { readPublishing, rememberPublishing } from '../components/PublishedBeat';
import {
  BoardSkeleton,
  CautionNotice,
  ErrorNotice,
  SuccessNotice,
  errorMessage,
} from '../components/Status';
import { TextField } from '../components/Fields';
import { ApiRequestError } from '../lib/api';
import { formatDateTime } from '../lib/format';
import {
  emptyJobForm,
  fieldSentence,
  isHttpUrl,
  jobFormFromParsed,
  type JobFormState,
} from '../lib/job-form';
import { useConfirmReservation, useMyJobs, useParseJd, useSubmitContent } from '../lib/queries';

/** How long the page waits on a webhook before it says so plainly. */
const MAX_POLLS = 8;
const POLL_MS = 1_500;
/** How many reads go by before the browser asks Stripe itself. */
const POLLS_BEFORE_CONFIRM = 2;

export function PostFinishPage(): ReactNode {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id') ?? undefined;
  // Read once. A refresh that lost the query string still has the id the
  // reserve step left behind before handing over to Stripe.
  const [jobId] = useState<string | undefined>(
    () => params.get('job') ?? readPublishing() ?? undefined,
  );

  const [reads, setReads] = useState(0);
  const confirmed = useRef(false);
  const confirm = useConfirmReservation();

  const query = useMyJobs({ refetchInterval: reads < MAX_POLLS ? POLL_MS : false });
  const listing: MyListing | undefined = query.data?.jobs.find((job) => job.id === jobId);
  const waiting = listing !== undefined && listing.status === 'PENDING_CONTENT';

  // Every completed read counts, including the first: the budget is on the
  // wait, not on the retries.
  useEffect(() => {
    if (query.dataUpdatedAt === 0) return;
    setReads((count) => count + 1);
  }, [query.dataUpdatedAt]);

  useEffect(() => {
    if (confirmed.current || !waiting) return;
    if (sessionId === undefined || reads < POLLS_BEFORE_CONFIRM) return;
    confirmed.current = true;
    confirm.mutate(sessionId);
  }, [confirm, reads, sessionId, waiting]);

  if (jobId === undefined) return <Navigate to="/dashboard" replace />;

  if (query.isLoading) {
    return (
      <div className="inner" style={{ paddingTop: 32 }}>
        <BoardSkeleton rows={3} />
      </div>
    );
  }
  if (query.isError) {
    return (
      <div className="inner" style={{ paddingTop: 32 }}>
        <ErrorNotice error={query.error} onRetry={() => void query.refetch()} />
      </div>
    );
  }

  const company = query.data?.company;

  return (
    <>
      <div style={{ borderBottom: '1px solid var(--hairline)', background: 'var(--canvas-soft)' }}>
        <div className="inner" style={{ paddingTop: 36, paddingBottom: 30 }}>
          <span className="eyebrow">Post a job</span>
          <h1 style={{ marginTop: 10, fontSize: 32 }}>Add the posting</h1>
          <p className="muted" style={{ marginTop: 10, maxWidth: '62ch' }}>
            Paste the link to the job posting and we’ll fill the form in for you. Check every field,
            then publish — your listing goes live immediately, and runs {LISTING_LIFETIME_DAYS}{' '}
            days.
          </p>
        </div>
      </div>

      <section className="section is-last" style={{ paddingTop: 32 }}>
        <div className="inner">
          {listing === undefined ? (
            <CautionNotice>
              We could not find that reservation on your account.{' '}
              <Link to="/dashboard">Open your dashboard</Link> to see everything you hold.
            </CautionNotice>
          ) : hasListingContent(listing) ? (
            <SuccessNotice>
              This listing already has its details.{' '}
              <Link to="/dashboard">Open your dashboard</Link> to see it.
            </SuccessNotice>
          ) : isPaidAwaitingContent(listing) && company !== undefined ? (
            <ContentStep listing={listing} company={company} />
          ) : reads >= MAX_POLLS ? (
            <CautionNotice>
              Stripe has not confirmed this payment yet. Nothing is lost — the reservation appears
              on <Link to="/dashboard">your dashboard</Link> the moment it clears, and you can
              finish the listing from there.{' '}
              <button type="button" className="linkbtn" onClick={() => setReads(0)}>
                Check again
              </button>
            </CautionNotice>
          ) : (
            <div className="card is-padded" role="status">
              <div className="row" style={{ gap: 12, alignItems: 'center' }}>
                <span
                  className="skeleton"
                  style={{ width: 18, height: 18, borderRadius: 999, flexShrink: 0 }}
                  aria-hidden="true"
                />
                <div>
                  <strong>Payment received. Confirming with Stripe…</strong>
                  <p className="small muted" style={{ marginTop: 4 }}>
                    This usually takes a second or two. Nothing is charged twice.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* The import, and the form it fills                                          */
/* -------------------------------------------------------------------------- */

type ImportOutcome =
  | { readonly kind: 'skipped' }
  | { readonly kind: 'parsed'; readonly result: Extract<ParseJdResponse, { ok: true }> }
  | {
      readonly kind: 'unavailable';
      readonly message: string;
      readonly remaining: number;
    }
  | { readonly kind: 'refused'; readonly message: string; readonly resetAt?: string };

function ContentStep({
  listing,
  company,
}: {
  readonly listing: MyListing;
  readonly company: Company;
}): ReactNode {
  const navigate = useNavigate();
  const parse = useParseJd();
  const submit = useSubmitContent();

  const [url, setUrl] = useState('');
  const [urlError, setUrlError] = useState<string | undefined>(undefined);
  const [prefill, setPrefill] = useState<JobFormState | undefined>(undefined);
  const [outcome, setOutcome] = useState<ImportOutcome | undefined>(undefined);
  const [elapsed, setElapsed] = useState(0);

  // The wait is real and can run to twenty seconds. A count of seconds is the
  // truth; a progress bar over an unknown duration is not.
  useEffect(() => {
    if (!parse.isPending) {
      setElapsed(0);
      return;
    }
    const started = Date.now();
    const timer = window.setInterval(
      () => setElapsed(Math.floor((Date.now() - started) / 1000)),
      1_000,
    );
    return () => window.clearInterval(timer);
  }, [parse.isPending]);

  async function runImport(): Promise<void> {
    const trimmed = url.trim();
    if (trimmed === '') {
      setUrlError('Paste the address of the job posting.');
      return;
    }
    if (!isHttpUrl(trimmed)) {
      setUrlError('Enter a full link, beginning http:// or https://.');
      return;
    }
    setUrlError(undefined);

    const fallback = (): JobFormState => ({ ...emptyJobForm(), applyUrl: trimmed });

    try {
      const result = await parse.mutateAsync({ url: trimmed, jobId: listing.id });
      if (result.ok) {
        setPrefill(jobFormFromParsed(result.fields));
        setOutcome({ kind: 'parsed', result });
      } else {
        setPrefill(fallback());
        setOutcome({
          kind: 'unavailable',
          message: result.message,
          remaining: result.quota.remaining,
        });
      }
    } catch (error) {
      // Every real error ends in the same place the successful import does:
      // the form. What changes is the sentence above it.
      const resetAt =
        error instanceof ApiRequestError && typeof error.details?.['resetAt'] === 'string'
          ? (error.details['resetAt'] as string)
          : undefined;
      setPrefill(fallback());
      setOutcome({
        kind: 'refused',
        message: errorMessage(error),
        ...(resetAt === undefined ? {} : { resetAt }),
      });
    }
  }

  const reserved = listing.totalPaidCents;

  if (prefill === undefined) {
    return (
      <div className="stack" style={{ gap: 20 }}>
        <SuccessNotice>
          <Money cents={reserved} /> reserved for this listing. It publishes as soon as you add the
          posting below.
        </SuccessNotice>

        <div className="card is-padded">
          <span className="eyebrow">Step 3 — the posting</span>
          <h2 style={{ marginTop: 8 }}>Paste the link to your job posting</h2>
          <p className="muted" style={{ marginTop: 8, maxWidth: '58ch' }}>
            Paste it and we’ll fill the form in for you — the title, the description, the salary,
            wherever the page states them. You check and correct every field before anything is
            published.
          </p>

          <div style={{ marginTop: 20 }}>
            <TextField
              label="Job posting URL"
              value={url}
              onChange={setUrl}
              placeholder="https://jobs.example.com/careers/senior-engineer"
              type="url"
              disabled={parse.isPending}
              error={urlError}
              hint="Your own careers page, or any board you already posted on."
            />
          </div>

          {parse.isPending ? (
            <p className="small muted" role="status" style={{ marginTop: 12 }}>
              Reading the posting… {elapsed}s. Some pages take up to twenty seconds.
            </p>
          ) : null}

          <div className="row" style={{ marginTop: 18, gap: 12 }}>
            <button
              type="button"
              className="cta"
              style={{ marginTop: 0, width: 'auto' }}
              disabled={parse.isPending}
              onClick={() => void runImport()}
            >
              {parse.isPending ? 'Reading the posting…' : 'Read the posting'}
            </button>
            <button
              type="button"
              className="pill pill-quiet"
              disabled={parse.isPending}
              onClick={() => {
                setPrefill(emptyJobForm());
                setOutcome({ kind: 'skipped' });
              }}
            >
              Skip — I’ll fill it in myself
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="stack" style={{ gap: 20 }}>
      <SuccessNotice>
        <Money cents={reserved} /> reserved for this listing. It publishes the moment you submit
        the details below.
      </SuccessNotice>

      <ImportNotice
        outcome={outcome}
        onRetry={() => {
          parse.reset();
          setPrefill(undefined);
          setOutcome(undefined);
        }}
      />

      <JobForm
        key={outcome?.kind ?? 'empty'}
        initial={prefill}
        submitLabel="Publish this listing"
        busy={submit.isPending}
        preview={{ company, paidCents: reserved }}
        serverError={submit.isError ? errorMessage(submit.error) : undefined}
        footerNote={
          <>
            Submitting publishes this listing immediately — it is already paid for. It runs{' '}
            {LISTING_LIFETIME_DAYS} days and renews free.
          </>
        }
        onSubmit={async (input) => {
          await submit.mutateAsync({ id: listing.id, input });
          // The dashboard's beat needs to know which listing to celebrate.
          rememberPublishing(listing.id);
          void navigate('/dashboard?published=1');
        }}
      />
    </div>
  );
}

function ImportNotice({
  outcome,
  onRetry,
}: {
  readonly outcome: ImportOutcome | undefined;
  readonly onRetry: () => void;
}): ReactNode {
  if (outcome === undefined || outcome.kind === 'skipped') return null;

  if (outcome.kind === 'parsed') {
    const { filled, missing, quota } = outcome.result;
    return (
      <div className="notice is-positive">
        <span className="notice-icon" aria-hidden="true" />
        <span>
          <strong>We filled in {fieldSentence(filled)}.</strong> Check every field — the page is
          the source, and it can be wrong or out of date.
          {missing.length === 0 ? null : (
            <>
              {' '}
              The posting did not state {fieldSentence(missing)}, so you will need to add{' '}
              {missing.length === 1 ? 'it' : 'them'}.
            </>
          )}{' '}
          <span className="muted">
            Imports left today: {quota.remaining}.{' '}
            {quota.remaining > 0 ? (
              <button type="button" className="linkbtn" onClick={onRetry}>
                Try a different link
              </button>
            ) : null}
          </span>
        </span>
      </div>
    );
  }

  if (outcome.kind === 'unavailable') {
    return (
      <CautionNotice>
        {outcome.message} Fill the form in below — nothing about your reservation changes.{' '}
        {outcome.remaining > 0 ? (
          <button type="button" className="linkbtn" onClick={onRetry}>
            Try a different link
          </button>
        ) : null}
      </CautionNotice>
    );
  }

  return (
    <CautionNotice>
      {outcome.message}
      {outcome.resetAt === undefined ? null : <> Your imports reset {formatDateTime(outcome.resetAt)}.</>}{' '}
      Fill the form in below — nothing about your reservation changes.
    </CautionNotice>
  );
}
